import React, { FC, useEffect, useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import { MessageBundle } from '@amzn/arb-tools';
import InputController from '../../../shared/components/form/InputController';
import DropdownController from '../../../shared/components/form/DropdownController';
import { EMAIL_VALIDATION_REGEX } from '../../../shared/constants';
import {
  VAT_LABEL_KEY,
  GST_LABEL_KEY,
  CNPJ_LABEL_KEY,
  RFC_LABEL_KEY,
  PAN_LABEL_KEY,
  CDFE_LABEL_KEY,
  CDFE_DEFAULT_HINT_KEY,
  PEC_LABEL_KEY,
  KEP_LABEL_KEY,
  KEP_TOOLTIP_KEY,
  MX_TAX_CODE_LABEL_KEY,
  MX_TAX_CODE_DESCRIPTIONS,
  MX_TAX_CODE_DEFAULT_BILLING_MX,
  MX_TAX_CODE_DEFAULT_BILLING_NON_MX,
  EMAIL_ERROR_KEY,
  VAT_ERROR_KEY,
  CNPJ_ERROR_KEY,
  RFC_ERROR_KEY,
  PAN_ERROR_KEY,
  GST_ERROR_KEY,
  OPTIONAL_LABEL_KEY,
  REQUIRED_ERROR_KEY,
  SELECT_PLACEHOLDER_KEY,
  getTaxRequirements,
  CNPJ_VALIDATION_REGEX,
  RFC_VALIDATION_REGEX,
  PAN_VALIDATION_REGEX,
  GST_VALIDATION_REGEX_BY_COUNTRY,
  optionalVatValidator,
  VAT_NOT_APPLICABLE_COUNTRIES,
} from '../constants';

interface TaxFieldsProps {
  intl: MessageBundle;
}

/**
 * Country-conditional tax inputs for the DSP activation request KYC form.
 * Mirrors what D16GAdspActivationService's CountryTaxTranslator subclasses
 * require.
 *
 * The MX TAX CODE dropdown follows
 * D16GAdspActivation-UIComponents/billing/CountryStateAndTaxCode.tsx: it is
 * always rendered when marketplace == MX, disabled when billing country is
 * not MX, with default value '601' for MX billing and '616' otherwise.
 */
const TaxFields: FC<TaxFieldsProps> = ({ intl }) => {
  const { watch, setValue, getValues } = useFormContext();
  const billingCountry = (watch('country') as string) || '';
  const marketplace = (watch('marketplace') as string) || '';

  const required = intl.getMessage(REQUIRED_ERROR_KEY);
  const optional = intl.getMessage(OPTIONAL_LABEL_KEY);
  const select = intl.getMessage(SELECT_PLACEHOLDER_KEY);
  const emailError = intl.getMessage(EMAIL_ERROR_KEY);
  const vatError = intl.getMessage(VAT_ERROR_KEY);
  const cnpjError = intl.getMessage(CNPJ_ERROR_KEY);
  const rfcError = intl.getMessage(RFC_ERROR_KEY);
  const panError = intl.getMessage(PAN_ERROR_KEY);
  const gstError = intl.getMessage(GST_ERROR_KEY);

  const requirements = getTaxRequirements(billingCountry);
  const gstRegex = GST_VALIDATION_REGEX_BY_COUNTRY[billingCountry];

  const mxTaxCodeItems = useMemo(
    () =>
      Object.entries(MX_TAX_CODE_DESCRIPTIONS).map(([code, description]) => ({
        value: code,
        label: `${code} (${description})`,
      })),
    [],
  );

  // Mirror legacy CountryStateAndTaxCode.updateMXCode: when marketplace is MX,
  // pre-fill the tax code based on whether billing country matches the
  // marketplace. Clear it when marketplace is no longer MX.
  useEffect(() => {
    const current = (getValues('mxTaxCode') as string) || '';
    if (marketplace !== 'MX') {
      if (current) setValue('mxTaxCode', '');
      return;
    }
    if (billingCountry === 'MX') {
      // For MX billing, seed with the MX default unless the user previously
      // had the non-MX default (i.e. they hadn't picked anything custom).
      if (!current || current === MX_TAX_CODE_DEFAULT_BILLING_NON_MX) {
        setValue('mxTaxCode', MX_TAX_CODE_DEFAULT_BILLING_MX);
      }
    } else if (!current || current === MX_TAX_CODE_DEFAULT_BILLING_MX) {
      setValue('mxTaxCode', MX_TAX_CODE_DEFAULT_BILLING_NON_MX);
    }
  }, [marketplace, billingCountry, getValues, setValue]);

  return (
    <>
      {billingCountry &&
        requirements.vat !== 'none' &&
        !VAT_NOT_APPLICABLE_COUNTRIES.includes(billingCountry) && (
          <InputController
            label={intl.getMessage(VAT_LABEL_KEY)}
            id="vat-input"
            controllerName="vatNumber"
            optionalTag={optional}
            registerOptions={{
              validate: optionalVatValidator(billingCountry, vatError),
            }}
          />
        )}
      {requirements.gst !== 'none' && (
        <InputController
          label={intl.getMessage(GST_LABEL_KEY)}
          id="gst-input"
          controllerName="gstNumber"
          optionalTag={requirements.gst === 'optional' ? optional : undefined}
          registerOptions={{
            ...(requirements.gst === 'required' ? { required } : {}),
            ...(gstRegex
              ? { pattern: { value: gstRegex, message: gstError } }
              : {}),
          }}
        />
      )}
      {requirements.cnpj === 'required' && (
        <InputController
          label={intl.getMessage(CNPJ_LABEL_KEY)}
          id="cnpj-input"
          controllerName="cnpjNumber"
          registerOptions={{
            required,
            pattern: { value: CNPJ_VALIDATION_REGEX, message: cnpjError },
          }}
        />
      )}
      {requirements.rfc === 'required' && (
        <InputController
          label={intl.getMessage(RFC_LABEL_KEY)}
          id="rfc-input"
          controllerName="rfcNumber"
          registerOptions={{
            required,
            pattern: { value: RFC_VALIDATION_REGEX, message: rfcError },
          }}
        />
      )}
      {requirements.pan === 'required' && (
        <InputController
          label={intl.getMessage(PAN_LABEL_KEY)}
          id="pan-input"
          controllerName="panNumber"
          registerOptions={{
            required,
            pattern: { value: PAN_VALIDATION_REGEX, message: panError },
          }}
        />
      )}
      {requirements.cdfe === 'required' && (
        <InputController
          label={intl.getMessage(CDFE_LABEL_KEY)}
          id="cdfe-input"
          controllerName="cdfeNumber"
          helpTipText={intl.getMessage(CDFE_DEFAULT_HINT_KEY)}
          optionalTag={optional}
        />
      )}
      {requirements.pec === 'required' && (
        <InputController
          label={intl.getMessage(PEC_LABEL_KEY)}
          id="pec-input"
          controllerName="pecEmail"
          registerOptions={{
            required,
            pattern: { value: EMAIL_VALIDATION_REGEX, message: emailError },
          }}
        />
      )}
      {requirements.kep === 'optional' && (
        <InputController
          label={intl.getMessage(KEP_LABEL_KEY)}
          id="kep-input"
          controllerName="kepEmail"
          optionalTag={optional}
          helpTipText={intl.getMessage(KEP_TOOLTIP_KEY)}
          registerOptions={{
            pattern: { value: EMAIL_VALIDATION_REGEX, message: emailError },
          }}
        />
      )}
      {marketplace === 'MX' && (
        <DropdownController
          label={intl.getMessage(MX_TAX_CODE_LABEL_KEY)}
          overrideDropdownLabel={select}
          id="mx-tax-code"
          controllerName="mxTaxCode"
          items={mxTaxCodeItems}
          registerOptions={{ required }}
          disabled={billingCountry !== 'MX'}
        />
      )}
    </>
  );
};

export default TaxFields;
